export const CUSTOMER_MEMO_TEMPLATE_VARIABLE_TOKENS = ['{고객명}', '{담당자명}', '{오늘날짜}', '{연락처}', '{회사명}']

const TOKEN_RE = /\{([^{}\s]{1,40})\}/g

function firstText(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

function todayText() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function collectText(value, out = []) {
  if (typeof value === 'string') out.push(value)
  else if (Array.isArray(value)) value.forEach((item) => collectText(item, out))
  else if (value && typeof value === 'object') Object.values(value).forEach((item) => collectText(item, out))
  return out
}

export function extractCustomerMemoTemplateVariables(value) {
  const found = new Set()
  collectText(value).forEach((text) => {
    String(text || '').replace(TOKEN_RE, (_, key) => {
      found.add(`{${key}}`)
      return ''
    })
  })
  return Array.from(found)
}

function replaceAllVariables(text, variables) {
  let out = String(text ?? '')
  Object.entries(variables || {}).forEach(([key, value]) => {
    out = out.replaceAll(`{${key}}`, String(value ?? ''))
  })
  return out
}

export function buildCustomerMemoTemplateVariables({ customer, user } = {}) {
  return {
    고객명: firstText(customer?.customerName, customer?.name, customer?.customer, customer?.displayName),
    담당자명: firstText(user?.name, user?.displayName, user?.username, user?.email),
    오늘날짜: todayText(),
    연락처: firstText(customer?.customerPhone, customer?.phone, customer?.phoneNumber, customer?.mobilePhone, customer?.customerPhoneDigits),
    회사명: firstText(customer?.companyName, customer?.affiliation, customer?.company, customer?.orgName, user?.companyName, user?.organizationName, user?.orgName),
  }
}

export function analyzeCustomerMemoTemplateVariables(template, variables = {}) {
  const supported = new Set(CUSTOMER_MEMO_TEMPLATE_VARIABLE_TOKENS)
  const tokens = extractCustomerMemoTemplateVariables([template?.title, template?.plainText, template?.content])
  const unknownTokens = tokens.filter((token) => !supported.has(token))
  const emptyTokens = tokens.filter((token) => {
    const key = token.slice(1, -1)
    return supported.has(token) && !String(variables[key] ?? '').trim()
  })
  return {
    tokens,
    unknownTokens,
    emptyTokens,
    warnings: [
      ...unknownTokens.map((token) => `지원하지 않는 변수 ${token}가 있습니다.`),
      ...emptyTokens.map((token) => `${token}에 넣을 값이 없습니다.`),
    ],
  }
}

export function replaceCustomerMemoTemplateVariables(value, variables) {
  if (typeof value === 'string') return replaceAllVariables(value, variables)
  if (Array.isArray(value)) return value.map((item) => replaceCustomerMemoTemplateVariables(item, variables))
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, replaceCustomerMemoTemplateVariables(item, variables)]))
  }
  return value
}

export function applyCustomerMemoTemplateVariables(template, variables) {
  const analysis = analyzeCustomerMemoTemplateVariables(template, variables)
  return {
    ...template,
    title: replaceCustomerMemoTemplateVariables(template?.title || '', variables),
    plainText: replaceCustomerMemoTemplateVariables(template?.plainText || '', variables),
    content: replaceCustomerMemoTemplateVariables(template?.content || null, variables),
    variableWarnings: analysis.warnings,
    variableAnalysis: analysis,
  }
}

export function recordCustomerMemoTemplateUsage(api, template, context = {}) {
  const id = template?.id
  if (!id) return
  try {
    const body = context?.customerRefId ? { customerRefId: context.customerRefId } : null
    api.post(`/customer-management/customer-memo-templates/${id}/usage`, body, { __silent: true }).catch(() => {})
  } catch {
    // Usage stats should never block memo drafting.
  }
}
export function hasCustomerMemoDraftContent({ title, plainText, category, tagsText, attachments } = {}) {
  return Boolean(
    String(title || '').trim() ||
    String(plainText || '').trim() ||
    String(category || '').trim() ||
    String(tagsText || '').trim() ||
    (Array.isArray(attachments) && attachments.length > 0)
  )
}

function tagTextFrom(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  return String(value || '')
}

function mergeTagsText(...values) {
  const out = []
  values.join(',').split(/[#,]+/).forEach((raw) => {
    const tag = raw.trim()
    if (!tag) return
    if (!out.some((item) => item.toLowerCase() === tag.toLowerCase())) out.push(tag)
  })
  return out.join(', ')
}

export function mergeCustomerMemoTemplateDraft(current, applied, mode = 'replace') {
  if (mode === 'append') {
    return {
      title: current?.title || applied?.title || '',
      plainText: [current?.plainText, applied?.plainText].map((item) => String(item || '').trim()).filter(Boolean).join('\n\n'),
      content: null,
      category: current?.category || applied?.category || '',
      tagsText: mergeTagsText(current?.tagsText, tagTextFrom(applied?.tags)),
      attachments: Array.isArray(current?.attachments) ? current.attachments : [],
    }
  }
  return {
    title: applied?.title || '',
    plainText: applied?.plainText || '',
    content: applied?.content || null,
    category: applied?.category || '',
    tagsText: tagTextFrom(applied?.tags),
    attachments: [],
  }
}

export function chooseCustomerMemoTemplateApplyMode(hasDraft) {
  if (!hasDraft) return 'replace'
  if (window.confirm('작성 중인 메모 뒤에 템플릿 내용을 이어붙일까요?')) return 'append'
  if (window.confirm('기존 작성 내용을 템플릿으로 덮어쓸까요?')) return 'replace'
  return null
}